import { call, takeLatest, takeEvery, put, all } from 'redux-saga/effects';

import {
  getNotificationConfigs,
  getMembers as getMembersApi,
  getUsers as getUsersApi,
  createNotificationConfig as createApi,
  updateNotificationConfig as updateApi,
  deleteNotificationConfig as deleteApi,
} from '../../services';
import { displayNotification, checkOnline, getNotification } from '../notifications';
import { getRoleDefaults } from '../../components/SiteAdminView/NotificationView/constants';
import {
  FETCH_NOTIFICATION_CONFIGS,
  FETCH_USER_NOTIFICATION_CONFIGS,
  CREATE_NOTIFICATION_CONFIG,
  UPDATE_NOTIFICATION_CONFIG,
  DELETE_NOTIFICATION_CONFIG,
  SYNC_NOTIFICATION_CONFIGS,
  RESET_ALL_NOTIFICATION_CONFIGS,
  receiveNotificationConfigs,
  receiveUserNotificationConfigs,
  setUserNotificationConfigsLoading,
  configCreated,
  configUpdated,
  configDeleted,
  setNotificationConfigsLoading,
} from './actions';

export function* doFetchNotificationConfigs(action) {
  const { query } = action;
  try {
    yield put(setNotificationConfigsLoading(true));
    const { data, total } = yield call(getNotificationConfigs, query);
    yield put(receiveNotificationConfigs(data, total));
  } catch (error) {
    console.error('Unable to fetch notification configs:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('getNotificationConfigs', 'error')()));
  } finally {
    yield put(setNotificationConfigsLoading(false));
  }
}

export function* doCreateNotificationConfig(action) {
  const { data } = action;
  try {
    const config = yield call(createApi, data);
    yield put(configCreated(config));
    yield put(displayNotification(getNotification('createNotificationConfig', 'success')()));
  } catch (error) {
    console.error('Unable to create notification config:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('createNotificationConfig', 'error')()));
  }
}

export function* doUpdateNotificationConfig(action) {
  const { id, data } = action;
  try {
    const config = yield call(updateApi, id, data);
    yield put(configUpdated(config));
    yield put(displayNotification(getNotification('updateNotificationConfig', 'success')()));
  } catch (error) {
    console.error('Unable to update notification config:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('updateNotificationConfig', 'error')()));
  }
}

export function* doDeleteNotificationConfig(action) {
  const { id } = action;
  try {
    yield call(deleteApi, id);
    yield put(configDeleted(id));
    yield put(displayNotification(getNotification('deleteNotificationConfig', 'success')()));
  } catch (error) {
    console.error('Unable to delete notification config:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('deleteNotificationConfig', 'error')()));
  }
}

export function* doFetchUserNotificationConfigs(action) {
  const { query } = action;
  try {
    yield put(setUserNotificationConfigsLoading(true));
    const { data, total } = yield call(getNotificationConfigs, {
      ...query,
      configType: 'user',
    });
    yield put(receiveUserNotificationConfigs(data, total));
  } catch (error) {
    console.error('Unable to fetch user notification configs:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('getNotificationConfigs', 'error')()));
  } finally {
    yield put(setUserNotificationConfigsLoading(false));
  }
}

function getEmailsForMember(member, users) {
  const userIds = (member.connected || [])
    .filter((c) => c.startsWith('users/'))
    .map((c) => c.split('/')[1]);
  return userIds
    .map((id) => users.find((u) => u.id === id))
    .filter((u) => u?.enabled && u?.email)
    .map((u) => u.email);
}

export function* doSyncNotificationConfigs(action) {
  const { org, site } = action;
  try {
    const [{ data: roleConfigs }, { values: siteMembers }, { values: users }] = yield all([
      call(getNotificationConfigs, { org, site, configType: 'role' }),
      call(getMembersApi, { objectId: `site/${site}` }),
      call(getUsersApi, { limit: 0 }),
    ]);

    yield put(receiveNotificationConfigs(roleConfigs, roleConfigs.length));

    const existingRoles = new Set(roleConfigs.map((c) => c.role));

    const missingRoles = siteMembers.filter((m) => !existingRoles.has(m.id));

    for (const member of missingRoles) {
      const defaults = getRoleDefaults(member.type);
      const emails = getEmailsForMember(member, users);
      const config = yield call(createApi, {
        eventType: 'dtmp',
        role: member.id,
        emails,
        org,
        site,
        stateChangeSettings: defaults.stateChangeSettings,
        enabled: true,
      });
      yield put(configCreated(config));
    }

    const emptyEmailConfigs = roleConfigs.filter((c) => !c.emails || c.emails.length === 0);
    for (const config of emptyEmailConfigs) {
      const member = siteMembers.find((m) => m.id === config.role);
      if (member) {
        const emails = getEmailsForMember(member, users);
        if (emails.length > 0) {
          yield call(updateApi, config.id, { emails });
        }
      }
    }
  } catch (error) {
    console.error('Unable to sync notification configs:', error);
    yield call(checkOnline);
  }
}

export function* doResetAllNotificationConfigs(action) {
  const { org, site } = action;
  try {
    const [{ data: roleConfigs }, { data: userConfigs }] = yield all([
      call(getNotificationConfigs, { org, site, configType: 'role' }),
      call(getNotificationConfigs, { org, site, configType: 'user' }),
    ]);

    for (const config of [...roleConfigs, ...userConfigs]) {
      yield call(deleteApi, config.id);
    }

    yield put(receiveNotificationConfigs([], 0));
    yield put(receiveUserNotificationConfigs([], 0));
    yield call(doSyncNotificationConfigs, { org, site });
    yield put(displayNotification(getNotification('resetAllNotificationConfigs', 'success')()));
  } catch (error) {
    console.error('Unable to reset all notification configs:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('resetAllNotificationConfigs', 'error')()));
  }
}

export const sagas = [
  takeLatest(FETCH_NOTIFICATION_CONFIGS, doFetchNotificationConfigs),
  takeLatest(FETCH_USER_NOTIFICATION_CONFIGS, doFetchUserNotificationConfigs),
  takeEvery(CREATE_NOTIFICATION_CONFIG, doCreateNotificationConfig),
  takeLatest(UPDATE_NOTIFICATION_CONFIG, doUpdateNotificationConfig),
  takeEvery(DELETE_NOTIFICATION_CONFIG, doDeleteNotificationConfig),
  takeLatest(SYNC_NOTIFICATION_CONFIGS, doSyncNotificationConfigs),
  takeLatest(RESET_ALL_NOTIFICATION_CONFIGS, doResetAllNotificationConfigs),
];
